<template>
  <section class="tk-product-wrapper">
    <button class="tk-back-button btn" @click="handleBackButton()">
      <img
        data-v-22b8f552=""
        src="@/assets/icons/ui/chevron-back-outline.png"
        alt="back arrow"
      />
      <span>back to browsing</span>
    </button>
    <div class="tk-product">
      <!-- Navigation / Steps -->
      <section class="tk-product-header">
        <div class="tk-product-header-product-name">
          <ProductBrandIcon
            v-if="
              activeProduct &&
              activeProduct.style_id &&
              activeProduct.brand &&
              productBrandingIconIsValid(activeProduct.brand)
            "
            :iconName="activeProduct.brand"
          />
          <h1 class="slate-bold">
            {{ activeProduct.name }}
          </h1>
          <p class="slate-bold">
            {{
              activeProduct.style_id ||
              activeProduct.styleId ||
              activeProduct.alias
            }}
          </p>
        </div>

        <!-- Steps Navigation -->
        <div id="tk-product-steps">
          <div
            class="navigation-item"
            :class="{
              disabled:
                step.name === activeDecoration ||
                ('color-designs' && activeDecoration),
              active: step.id === currentStep,
              'locker-step':
                step.id === addToLockerStepId || step.id === saveChangesStepId,
            }"
            v-for="(step, i) in navigationSteps"
            :key="i + '-n-step'"
            @click="handleSetStep(step.id)"
          >
            <!-- Design and Decorate -->
            <!-- Checkmark -->
            <div class="nagivation-step-container">
              <!-- Add to locker cart icon -->
              <img
                v-if="step.id === addToLockerStepId"
                src="@/assets/icons/ui/cart-outline-add.png"
                alt="cart icon"
              />
              <!-- Add to locker cart icon -->
              <img
                v-else-if="step.id === saveChangesStepId"
                src="@/assets/icons/ui/cloud-done-outline.svg"
                alt="cart icon"
              />
              <div
                v-else
                class="navigation-check"
                :class="{ checked: stepIsCompleted(step) }"
              >
                <div class="checkmark"></div>
              </div>

              <div class="navigation-label">{{ step.label }}</div>
            </div>
            <div class="border-bottom"></div>
          </div>
        </div>
      </section>

      <!-- Menu to make actions and preview the Active Product-->

      <section class="tk-product-content">
        <!-- Product preview -->
        <ActiveProductPreview
          :isActiveProductReversible="isActiveProductReversible"
          @changeReversibleView="handleChangeReversibleView()"
        />

        <!-- Customization Menu -->

        <keep-alive>
          <component
            :is="displayedComponent"
            :product="activeProduct"
            :modalLoading="modalLoading"
            @back="handleBackStep()"
            @next="handleNextStep()"
            @changeReversibleView="handleChangeReversibleView()"
            @hideEditableProduct="showEditableProduct = false"
            @showEditableProduct="showEditableProduct = true"
            @openPreviewNewLockerProducts="handleProductWasAddedToLocker"
            @setLoading="setLoading"
            @unsetLoading="unsetLoading"
          />
        </keep-alive>
      </section>
    </div>
    <!-- Loader -->
    <transition name="fade">
      <div class="tk-modal-loader" v-if="loadingThreekit">
        <Loader :withMessage="true" :message="threekitLoaderMessage" />
      </div>
    </transition>

    <!-- Locker Preview -->
    <transition name="fade">
      <NewLockerProductsPreviewModal
        v-if="showLockerPreviewModalOpen"
        :isModalOpen="showLockerPreviewModalOpen"
        :lockerProductsIds="previewLockerProductsIds"
        @onCloseModal="TOGGLE_LOCKER_PREVIEW_MODAL()"
        @goToProject="handleRedirectToProject()"
        @redirectToLocker="redirectToLocker()"
      />
    </transition>
  </section>
</template>

<script>
import ActiveProductColorsAndDesigns from "@/components/Projects/Project/ActiveProduct/ActiveProductColorsAndDesigns";
import ActiveProductDecorations from "@/components/Projects/Project/ActiveProduct/ActiveProductDecorations";
import ActiveProductPreview from "@/components/Projects/Project/ActiveProduct/ActiveProductPreview";
import ProductDisplay from "@/components/Projects/Product/ProductDisplay/ProductDisplay.vue";
import { mapActions, mapGetters, mapState, mapMutations } from "vuex";
import Loader from "@/components/Utils/Loader.vue";

export default {
  name: "ActiveProduct",

  data() {
    return {
      currentStep: 1,
      showEditableProduct: false,
      modalLoading: false,
    };
  },

  computed: {
    ...mapState("project", [
      "activeProduct",
      "editableProduct",
      "typeOfProject",
      "showLockerPreviewModalOpen",
    ]),
    ...mapState("auth", ["dealerMode"]),
    ...mapGetters("catalog", [
      "getProductBrandIcon",
      "productBrandingIconIsValid",
    ]),
    ...mapGetters("project", ["is3DAvailable", "isSnapsheet"]),
    ...mapGetters("threekit", [
      "isActiveProductReversible",
      "activeProductPrincipalColors",
    ]),
    ...mapState("threekit", {
      activeDecoration: (state) => state.activeDecoration,
      loadingThreekit: (state) => state.loading,
      completeLoader: (state) => state.completeLoader,
      threekitLoaderMessage: (state) => state.loadingMessage,
      isSublimated3D: (state) => state.isSublimated3D,
      reversibleView: (state) => state.reversibleView,
      cachedTKData: (state) => state.cachedTKData,
    }),
    ...mapState(["lockerModule"]),

    addToLockerStepId() {
      return this.steps?.find((step) => step.name === "add-to-locker")?.id;
    },

    steps() {
      const principalSteps = [
        {
          label: this.isSublimated3D ? "Customize" : "Choose Color",
          name: "color-designs",
          id: 1,
        },
        { label: "Decorate", name: "decorations", id: 2 },
      ];

      const addToLockerStep = {
        label: this.activeProduct?.short_id ? "Save Changes" : "Add to Locker",
        name: "add-to-locker",
        id: 3,
      };

      const saveChangesToProjectStep = {
        label: `Save to ${this.typeOfProject}`,
        name: "save-changes",
        id: 3,
      };

      //For snapsheets, when the user is in dealer mode, we show "Save to snapsheet" button
      if (
        this.isSnapsheet &&
        this.dealerMode &&
        !this.activeProduct.lockerProductId
      ) {
        principalSteps.push(saveChangesToProjectStep);
      } else {
        principalSteps.push(addToLockerStep);
      }

      return principalSteps;
    },

    displayedComponent() {
      const step = this.steps[this.currentStep - 1];
      switch (step.name) {
        case "color-designs":
          //todo -> if entre stock y sublimations para mostrar otro componente
          return "ActiveProductColorsAndDesigns";

        case "decorations":
          return "ActiveProductDecorations";
        case "add-to-locker":
          return "ActiveProductDecorations";
        default:
          return "none";
      }
    },

    navigationSteps() {
      //temporalmente vamos a mostrar solo los 3 primeros
      return this.steps.slice(0, 3);
    },

    inheritedDesign() {
      return this.activeProduct.design;
    },

    previewLockerProductsIds() {
      if (this.activeProduct?.lockerProductId) {
        return [this.activeProduct.lockerProductId];
      }
      const quantityOfLockerProductsPreviewToDisplay = 1;
      const products = this.lockerModule.locker.products.slice(
        -Math.abs(quantityOfLockerProductsPreviewToDisplay)
      ); //Display the last 1, 2 or 3 elements of the locker

      return products.map((pr) => pr.lockerProductId);
    },

    saveChangesStepId() {
      return this.steps?.find((step) => step.name === "save-changes")?.id;
    },
  },

  methods: {
    ...mapActions("project", [
      "addToLocker",
      "getActiveProductPricing",
      "saveProductInBuilder",
      "setActiveProduct",
      "redirectToLocker",
      "redirectToProjectPage",
    ]),
    ...mapActions("threekit", [
      "changeReversibleView",
      "close3DDecorations",
      "getImageProofSnapshots",
      "initPlayerConfigurator",
      "saveActiveProductConfiguration",
      "setLoadingState",
    ]),
    ...mapActions("snapsheet", ["updateProductCustomChanges", "setProduct"]),
    ...mapActions("editableLogo", ["requestImageAsPNG"]),
    ...mapMutations("project", ["TOGGLE_LOCKER_PREVIEW_MODAL"]),

    async handleAddToLocker() {
      this.setLoadingState({
        loadingState: true,
        message: "Saving Changes...",
      });
      this.$nextTick(async () => {
        try {
          const threekitData = await this.saveActiveProductConfiguration();
          const builderConfig = await this.saveProductInBuilder({
            product: this.activeProduct,
            shortId: threekitData?.shortId,
          });
          console.log({ threekitData: threekitData });
          await this.addToLocker({
            product: this.activeProduct,
            builderConfig,
            shortId: threekitData?.shortId,
          });

          this.TOGGLE_LOCKER_PREVIEW_MODAL();
        } catch (error) {
          console.log(error);
        } finally {
          this.setLoadingState({ loadingState: false, message: "" });
        }
      });
    },

    handleChangeReversibleView() {
      const newView = this.reversibleView === "Inside" ? "Outside" : "Inside";
      this.setLoadingState({
        loadingState: true,
        message: "Applying Changes...",
      });
      this.$nextTick(() => {
        setTimeout(async () => {
          try {
            await this.changeReversibleView(newView);
          } catch (error) {
            console.log(error);
          } finally {
            this.setLoadingState({ loadingState: false });
          }
        }, 100);
      });
    },

    handleRedirectToProject() {
      this.redirectToProjectPage();
      this.TOGGLE_LOCKER_PREVIEW_MODAL();
    },

    async handleSaveChangesToProject() {
      this.setLoadingState({
        loadingState: true,
        message: "Saving Changes...",
      });
      this.$nextTick(async () => {
        try {
          const threekitData = await this.saveActiveProductConfiguration();
          const snapshots = await this.getImageProofSnapshots();

          const snapshotURL = await this.requestImageAsPNG(snapshots["Front"]);
          console.log({ threekitData, snapshots, snapshotURL });

          //Update snapsheet product
          const updatedProduct = await this.updateProductCustomChanges({
            wrapperId: this.activeProduct.wrapperId,
            threekitConfigId: threekitData?.shortId,
            threekitSavedPreview: snapshotURL,
          });
          this.setProduct({
            product: updatedProduct,
          });
          this.redirectToProjectPage();
          console.log({ updatedProduct });
        } catch (error) {
          console.log(error);
        } finally {
          this.setLoadingState({ loadingState: false, message: "" });
        }
      });
    },

    handleBackButton() {
      this.$router.go(-1);
    },

    handleBackStep() {
      const currentStepIndex = this.steps.findIndex(
        (step) => step.name === this.currentStep
      );
      this.currentStep = this.steps[currentStepIndex - 1].name;
    },

    handleNextStep() {
      const currentStepIndex = this.steps.findIndex(
        (step) => step.name === this.currentStep
      );
      this.currentStep = this.steps[currentStepIndex + 1].name;
      this.showEditableProduct = true;
    },

    async handleSetStep(selectedStepId) {
      //Add to locker button
      if (selectedStepId === this.addToLockerStepId) {
        await this.handleAddToLocker();
      }
      //Save changes button
      if (selectedStepId === this.saveChangesStepId) {
        await this.handleSaveChangesToProject();
      }

      this.currentStep = selectedStepId;
    },

    stepIsCompleted(step) {
      return this.currentStep > step.id;
    },

    handleProductWasAddedToLocker() {
      this.$emit("openPreviewNewLockerProducts");
      this.setActiveProduct({ product: null });
    },

    setLoading() {
      this.modalLoading = true;
    },

    unsetLoading() {
      this.modalLoading = false;
    },
  },

  async mounted() {
    //Call for the 3d player

    console.log("INIT ->", this.activeProduct);
    console.log(
      "this.activeProduct.designName:",
      this.activeProduct.designName
    );
    await this.initPlayerConfigurator({
      styleName: this.activeProduct.styleName,
      designName: this.activeProduct.designName,
    });
  },

  watch: {
    async activeProductPrincipalColors() {
      if (!this.isSublimated3D) {
        await this.getActiveProductPricing({ getDealerPrice: false });
      }
      await this.getActiveProductPricing({
        getDealerPrice: true,
      });
    },
  },

  beforeDestroy() {
    this.close3DDecorations();
  },

  components: {
    ActiveProductColorsAndDesigns,
    ActiveProductDecorations,
    ActiveProductPreview,
    Loader,
    ProductDisplay,
  },
};
Loader;
</script>

<style lang="scss" scoped>
.spinner-loader-container {
  margin: auto;
}
.tk-back-button {
  border-radius: 5px;
  display: block;
  padding: 0.2rem;
  transition: all 0.1s;
  text-transform: capitalize;
  background: transparent;
  display: flex;
  align-items: center;
  cursor: pointer;
  width: -webkit-fit-content;
  width: -moz-fit-content;
  width: fit-content;
  margin-bottom: 1rem;
  text-decoration: none;
  text-transform: uppercase;

  @media screen and (max-width: 768px) {
    margin-bottom: 0;
  }
}
.tk-product-wrapper {
  position: relative;
  position: relative;
  margin: auto;
  max-width: 1920px;
  padding-top: 1rem;
  width: 90%;
}
.tk-product {
  // height: 90vh;
  // max-height: px;
  margin: auto;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 3px;
  border: 1px solid $borderColor;

  .tk-product-header {
    display: flex;
    background: $grey-lightest;
    border-bottom: 1px solid $borderColor;
    height: 5rem;

    .tk-product-header-product-name {
      display: flex;
      align-items: center;
      padding: 1.5rem;
      width: 50%;
      * {
        margin-right: 1.5rem;
      }
      h1 {
        margin-right: 0.5rem;
        font-size: 1.75rem;
      }
      p {
        font-size: 1.25rem;
        color: $grey-dark;
        margin: 0;
      }
      img {
        max-width: 30%;
        max-height: 100%;
      }
    }

    #tk-product-steps {
      display: flex;
      padding-top: 0;
      width: 50%;
      justify-content: space-evenly;
      .navigation-item {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-evenly;
        width: 26%;
        cursor: pointer;
        position: relative;
        transition: text-decoration 0.2s;
        overflow: hidden;
        &:hover {
          text-decoration: underline;
        }

        &.disabled {
          pointer-events: none;
          cursor: not-allowed;
          opacity: 0.5;
        }

        &.active {
          .border-bottom {
            border-color: $playbookColor;
            visibility: visible;
          }
          .navigation-check {
            border: 2px solid $playbookColor;
          }
        }

        &.locker-step {
          &:hover {
            text-decoration: none !important;
            .nagivation-step-container {
              background-color: $playbookColor;
              * {
                color: white;
                text-transform: capitalize;
              }
              img {
                filter: invert(1);
              }
            }
          }
        }

        .nagivation-step-container {
          display: flex;
          align-items: center;
          justify-content: space-evenly;
          width: 100%;
          transition: background 0.2s;
          padding: 0.5rem 1rem;
          text-align: center;
          border-radius: 6px;
        }

        .navigation-label {
          transition: text-decoration 0.2s;
          font-size: 1.1rem;
          text-transform: capitalize;
        }

        .navigation-check {
          width: 21px;
          height: 21px;
          border-radius: 50%;
          border: 2px solid rgba(236, 237, 237, 1);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.1s;
          &.checked {
            background: transparent;
            border: 2px solid $playbookColor;
            .checkmark {
              /* display: inline-block; */
              transform: rotate(45deg);
              height: 9px;
              width: 4px;
              margin-bottom: 2px;
              margin-left: 1px;
              border-bottom: 1px solid $playbookColor;
              border-right: 1px solid $playbookColor;
              transition: all 0.1s;
            }
          }
        }

        .border-bottom {
          border-radius: 20px;
          border-top: 4px solid transparent;
          height: 10px;
          margin: 0 auto;
          width: 100%;
          visibility: hidden;
          position: absolute;
          bottom: -7px;
          left: 0;
          right: 0;
          transition: all 0.1s;
        }
      }
    }

    @media screen and (max-width: 768px) {
      flex-direction: column;
      height: 80px;

      .tk-product-header-product-name,
      #tk-product-steps {
        height: 50%;
        width: 100%;
        padding: 0.25rem;
        padding-bottom: 0;
      }

      .tk-product-header-product-name {
        justify-content: center;
        img {
          height: 20px;
          // width: 100%;
        }
      }
    }
  }

  .tk-product-content {
    display: flex;
    height: 90%;

    ::v-deep .tk-product-menu-container {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      width: 50%;
      position: relative;
      // max-height: 100vh;
      border-left: 1px solid $borderColor;
      z-index: 10;

      .component-wrapper {
        overflow-y: auto;
        padding: 2rem 1rem 1rem 2.5rem;
        display: flex;
        flex-grow: 1;
      }

      .footer-container {
        /* display: flex; */
        width: 100%;
        padding: 1rem;
        background: white;
        box-shadow: 0px -4px 20px 0px rgba(215, 216, 219, 0.5);
        border-top: 1px solid #d7d8db;
        position: sticky;
        bottom: 0;
        #navigation-buttons {
          display: flex;
          gap: 1.25rem;

          .btn {
            background: black;
            color: white;
            text-transform: uppercase;
            &.save {
              width: 40%;
            }
            &.cancel {
              width: 20%;
              background: none;
              border: 1px solid $grey-dark;
              color: $grey-dark;
            }
          }
          .btn-transparent {
            background: transparent;
            color: $grey-dark;
            display: flex;
            align-items: center;
            img {
              width: 18px;
              margin-top: 1px;
              margin-right: 3px;
            }
          }
        }
      }
    }
  }

  @media screen and (max-width: 1024px) {
    width: 90vw;
  }
}
.tk-modal-loader {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  background: rgba(255, 255, 255, 0.5);
  z-index: 100;
}
</style>
